import { LineItemElementProps } from "../pages/CreatorTools/LineItemDBCreationToolPage";
import { normalizeString } from "./utils";

/**
 * Returns true if the element at `partnerIndex`'s selected value declares `value` (at `valueIndex`)
 * as an acceptable dependency — i.e. the reverse direction of a value dependency.
 */
const partnerDeclaresValue = (
  allElements: LineItemElementProps[],
  selectedValues: string[],
  valueIndex: number,
  value: string,
  partnerIndex: number,
): boolean => {
  const partnerValue = selectedValues[partnerIndex];
  const reverseDeps = allElements[partnerIndex]?.valueDependencies?.[partnerValue];
  if (!reverseDeps) return false;
  return reverseDeps.some((dep) => dep.elementIndex === valueIndex && dep.parentValues.includes(value));
};

/**
 * A permutation passes the dependency check if every declared `valueDependencies` constraint is
 * satisfied by at least one of:
 *   1. the partner element's selected value is in the constraint's `parentValues`, OR
 *   2. the partner element's selected value declares the current value back (double-dependency).
 * The second case lets pairs survive when only one side has explicitly opted into the other,
 * even if the other side's listed `parentValues` excludes it.
 */
const permutationSatisfiesDependencies = (allElements: LineItemElementProps[], selectedValues: string[]): boolean => {
  for (let i = 0; i < allElements.length; i++) {
    const element = allElements[i];
    const value = selectedValues[i];
    const deps = element.valueDependencies?.[value];
    if (!deps) continue;
    for (const { elementIndex, parentValues } of deps) {
      if (parentValues.includes(selectedValues[elementIndex])) continue;
      if (partnerDeclaresValue(allElements, selectedValues, i, value, elementIndex)) continue;
      return false;
    }
  }
  return true;
};

/**
 * Recursively generate the cartesian product of every element's acceptable values, deferring
 * dependency validation until the full permutation has been assembled.
 */
const buildAllPermutationArrays = (selectedValues: string[], remainingElements: LineItemElementProps[]): string[][] => {
  if (remainingElements.length === 0) {
    return [selectedValues];
  }

  const firstElement = remainingElements[0];

  if (firstElement.type === "text") {
    return buildAllPermutationArrays([...selectedValues, firstElement.value], remainingElements.slice(1));
  }

  const acceptableValues = (firstElement.acceptableValues ?? []).filter((v) => !!v);
  if (!!firstElement.allowEmptyValue) acceptableValues.push("");

  return acceptableValues.flatMap((acceptableValue) =>
    buildAllPermutationArrays([...selectedValues, acceptableValue], remainingElements.slice(1)),
  );
};

const constructPermutationArrays = (allElements: LineItemElementProps[]): string[][] => {
  return buildAllPermutationArrays([], allElements).filter((selectedValues) =>
    permutationSatisfiesDependencies(allElements, selectedValues),
  );
};

export const getAllValidPermutations = (allElements: LineItemElementProps[]): string[] => {
  return constructPermutationArrays(allElements).map((array) => normalizeString(array.join(" ")));
};

export const getAllValidPermutationArrays = (allElements: LineItemElementProps[]): string[][] => {
  return constructPermutationArrays(allElements);
};
